import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { stockService } from "../../services/stockService";
import { Product } from "../../types";

export interface StockAdjustmentResult {
    newQuantity: number;
}

interface Props {
    open: boolean;
    onClose: () => void;
    product: Product | null;
    currentStock: number;
    onSuccess: (result: StockAdjustmentResult) => void;
}

export const StockAdjustmentDialog: React.FC<Props> = ({
    open,
    onClose,
    product,
    currentStock,
    onSuccess,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [adjustmentType, setAdjustmentType] = useState<"add" | "set">("add");
    const [quantity, setQuantity] = useState<number>(0);
    const [reason, setReason] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const newTotal =
        adjustmentType === "add" ? currentStock + quantity : quantity;

    const handleClose = () => {
        setQuantity(0);
        setReason("");
        setError("");
        onClose();
    };

    const handleSubmit = async () => {
        if (!product) return;
        try {
            setLoading(true);
            setError("");
            if (adjustmentType === "add") {
                await stockService.addStock(
                    product.product_id,
                    quantity,
                    "MANUAL",
                    reason,
                );
            } else {
                await stockService.adjustStock(
                    product.product_id,
                    quantity,
                    reason,
                );
            }
            onSuccess({ newQuantity: newTotal });
            handleClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to adjust stock");
        } finally {
            setLoading(false);
        }
    };

    const isSubmitDisabled =
        loading ||
        (adjustmentType === "add" ? quantity <= 0 : quantity < 0);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 1,
                }}
            >
                <Box
                    sx={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    Adjust Stock{product?.name ? ` — ${product.name}` : ""}
                </Box>
                {isMobile && (
                    <IconButton
                        edge="end"
                        onClick={handleClose}
                        size="small"
                        sx={{ flexShrink: 0, ml: 1 }}
                    >
                        <Close />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current stock: <strong>{currentStock} units</strong>
                    </Typography>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Adjustment Type</InputLabel>
                        <Select
                            value={adjustmentType}
                            onChange={(e) =>
                                setAdjustmentType(e.target.value as "add" | "set")
                            }
                            label="Adjustment Type"
                        >
                            <MenuItem value="add">Add Stock</MenuItem>
                            <MenuItem value="set">Set Stock Level</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label={
                            adjustmentType === "add"
                                ? "Quantity to Add"
                                : "New Stock Level"
                        }
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                        sx={{ mt: 2 }}
                        inputProps={{ min: 0 }}
                    />

                    <TextField
                        fullWidth
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        sx={{ mt: 2 }}
                        multiline
                        rows={2}
                        placeholder="Enter reason for stock adjustment..."
                    />

                    <Alert severity="info" sx={{ mt: 2 }}>
                        New stock level will be: <strong>{newTotal} units</strong>
                    </Alert>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSubmitDisabled}
                >
                    {loading ? "Adjusting…" : "Adjust Stock"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
